import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import qs from 'qs';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import range from 'lodash/range';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createTable from './components/table';

const calendar = createCalendar({
	namespace: 'payoutLogTransfers',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'payoutLogTransfers',
	stateNodeName: 'filters',
	listOfFilters: [{
		name: 'affiliate_id',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'admin_id',
		title: 'Admins',
		placeholder: 'Add admins',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	}]
});

const table = createTable({
	namespace: 'payoutLogTransfers',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'payout-log-transfers/fetch-data-success';
const FETCH_DATA_ERROR = 'payout-log-transfers/fetch-data-error';
const SHOW_PROGRESS_BAR = 'payout-log-transfers/show-progress-bar';
const SELECT_CURRENCY = 'payout-log-transfers/select-currency';
const GOTO_PAGE_SUCCESS = 'payout-log-transfers/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'payout-log-transfers/toggle-progress-bar';
const APPLY = 'payout-log-transfers/apply';
const CLEAR = 'payout-log-transfers/clear';
const SET_SEARCH_QUERY = 'payout-log-transfers/set-search-query';

// permissions
export const allowedPermissions = {
	'view': 'payout_log_view'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	error: false,
	calendar: calendar.initialState,
	filters: filters.initialState,
	query: {
		date_range: {
			start: null,
			end: null
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	table: table.initialState,
	search: ''
};

// actions
export const actions = Object.assign({
	fetchData: props => (dispatch, getState) => {
		const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
		const savedFilters = filters.selectors.getSavedFilters(getState(), 'payoutLogTransfers');

		if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
			return dispatch(replace(`payout-log/transfers?${qs.stringify(savedFilters)}`));
		}

		dispatch(request(getState, externalQuery)).then(() => {
			if (externalQuery.entity) {
				const filtersList = externalQuery.entity.filters || [];

				dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
			}
		});
	},

	search: () => (dispatch, getState) => {
		dispatch(actions.setSearchQuery());
		dispatch(request(getState));
	},

	setSearchQuery: () => (dispatch, getState) => {
		const searchForm = getFormValues('payoutLogTransfersSearchForm')(getState());
		const search = searchForm && searchForm.search || '';

		dispatch({
			type: SET_SEARCH_QUERY,
			payload: search
		});
	},

	apply: () => (dispatch, getState) => {
		const searchForm = getFormValues('payoutLogTransfersSearchForm')(getState());
		const search = searchForm && searchForm.search;

		dispatch({
			type: APPLY,
			payload: search
		});

		dispatch(actions.toggleProgressBar());
		dispatch(request(getState));
	},

	clear: () => dispatch =>  {
		dispatch(actions.toggleProgressBar());
		dispatch(replace('payout-log/transfers'));
		dispatch({ type: CLEAR });
	},

	showProgressBar: () => ({ type: SHOW_PROGRESS_BAR }),

	selectCurrency: id => (dispatch, getState) => {
		dispatch({
			type: SELECT_CURRENCY,
			payload: id
		});

		dispatch(actions.toggleProgressBar());
		dispatch(request(getState));
	},

	toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),

	gotoPage: page => (dispatch, getState) => {
		dispatch(actions.toggleProgressBar());

		dispatch({
			type: GOTO_PAGE_SUCCESS,
			payload: page
		});

		dispatch(request(getState));
	}
},
filters.actions,
calendar.actions,
table.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => initialState,
	[FETCH_DATA_SUCCESS]: (state, action) => {
		return {
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		};
	},
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[SELECT_CURRENCY]: (state, action) => ({
		...state,
		query: {
			...state.query,
			currency: action.payload
		}
	}),
	[GOTO_PAGE_SUCCESS]: (state, action) => ({
		...state,
		pagination: {
			...state.pagination,
			currentPage: action.payload
		}
	}),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
	[CLEAR]: state => initialState,
	[APPLY]: (state, action) => ({
		...state,
		pagination: initialState.pagination,
		search: action.payload
	}),
	[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
},
filters.reducers,
calendar.reducers,
table.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutLogTransfers.data,
	getIntl: intl,
	getProgressBar: state => state.payoutLogTransfers.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.payoutLogTransfers.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutLogTransfers.filters,
		state => state
	)(state),
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getCurrentCurrency: state => state.payoutLogTransfers.query.currency,
	getCurrentDateRange: state => state.payoutLogTransfers.query.date_range,
	getTable: table.selectors.getTable,
	getPagination: state => {
		const { perPage, total, currentPage } = state.payoutLogTransfers.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
	getSearchData: state => ({ search: state.payoutLogTransfers.search }),
	getError: state => state.payoutLogTransfers.error
};

// functions
const getRequestQuery = (state, externalQuery = {}) => {
	const {
		query, calendar, filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn },
		search
	} = cloneDeep(state);

	const params = cloneDeep(query);
	const dateRange = getDateRangeFromCalendar(calendar);
	const limit = perPage;
	const offset = (currentPage - 1) * perPage;

	params['limit'] = limit;
	params['offset'] = offset;
	params['sort'] = [sortByColumn.index, sortByColumn.direction];
	params['search'] = search;

	if (dateRange) {
		params['date_range'] = dateRange;
	}

	if (selectedFilters) {
		const filters = {};
		const inverseFilters = {};

		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});

		if (!isEmpty(filters)) {
			params['filters'] = filters;
		}
	}

	return qs.stringify({ ...params, ...externalQuery });
};

function request(getState, externalQuery) {
	const query = getRequestQuery(getState().payoutLogTransfers, externalQuery && externalQuery.query);

	return http({
		url: `${endpoints.PAYOUTS.TRANSFERS}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-log/transfers/index.js